/**
 * useAccessScope — Хэрэглэгчийн эрх/удирдлагын түвшингээр дата хамрах хүрээг
 * тодорхойлох дундын hook.
 *
 * risk/List.js:1148-1343-ийн scoping логикийг дахин ашиглагдахаар гаргасан.
 * Дата анализ зэрэг хуудсанд аюул/эрсдэл/ӨЭА-г үзэж буй хэрэглэгчийн хүрээгээр
 * client талд шүүхэд ашиглана.
 *
 * Түвшин:
 *   factory  — sso_owner эсвэл үйлдвэрийн хэмжээний бүлэг → бүх нэгж
 *   cex      — цех-түвшний бүлэг → зөвхөн өөрийн цех (tseh_code prefix)
 *   brigade  — анхан шатны удирдах ажилтан → зөвхөн өөрийн ХЭСЭГ
 *   self     — бусад → зөвхөн өөрийн бүртгэл (tn)
 *
 * ⚠ "brigade" яагаад хэрэгтэй вэ: ХЭМАБМТ-06 журмаар I шатны үзлэгийг ЭЭЛЖ
 * БҮР анхан шатны удирдах ажилтан (наряд өгөгч) хийдэг ба prework5a-ийн дүнг
 * үндэслэн ажилтныг ажилд гаргах эсэхийг шийддэг. Тэрээр бригадынхаа датаг
 * харах ёстой атлаа өмнө нь `self` болж унаж, зөвхөн ӨӨРИЙНХӨӨ бүртгэлийг
 * хардаг байв. Ялангуяа ӨЭА бүртгэл нь цехийн кодыг `department_code`-д,
 * хэсгийн кодыг `section_code`-д хадгалдаг тул `self` шүүлт огт таардаггүй.
 */
import { useMemo } from "react";
import { useUserContext } from "src/contexts/userContext";

// Үйлдвэрийн хэмжээ (бүх нэгж): ХАБ хэлтэс / админ / дээд удирдлага.
// ports.py group_full-тэй ЯГ ижил байх ёстой — өөр байвал backend нэгжийг
// шүүчихээд frontend шүүхгүй (эсвэл эсрэгээр) болж зөрүү гарна.
// ⚠ 380 (ХАБЭА хэлтсийн ХСБ — хор саармагжуулах хариуцсан мэргэжилтэн) энд
//   ОРОХГҮЙ. Тэр нь нэг чиглэлийн мэргэжилтэн тул бүх нэгжийн дата харахгүй.
//   380 нь departmentTseh.js-д БН сонгох жагсаалтыг нээх зорилгоор тусад нь
//   шалгагддаг — тэр нь сонголтын эрх, дата харах эрх биш.
const FACTORY_GROUPS = [173, 174, 240, 384, 400];
// Цех түвшин: цех дарга(166) / шат шатны удирдлага(167) / нэгжийн ХАБ(179) /
// цехийн орлогч дарга, ерөнхий инженер(192).
// backend user.py group_cex [166,179,192,167]-тэй нийцүүлэв.
const CEX_GROUPS = [166, 167, 179, 192];
// Анхан шатны удирдах ажилтан (наряд өгөгч) — зөвхөн өөрийн хэсэг/бригад.
// Журам: I шатны үзлэгийг ээлж бүр энэ хүн хийнэ (Хавсралт №2 + №8 дэвтэр).
const BRIGADE_GROUPS = [168];
// ХЯНАЖ БАТЛАХ эрхтэй бүлгүүд — БН дарга(166), бүрэн эрх(174),
// БН-ийн ХЭМАБ ажилтан(179), БН-ийн дэд удирдлага(192).
// risk/List.js:1153-тай ижил жагсаалт. Энэ нь ХАРАХ хүрээ биш — өөрийн
// хүрээнд БАТЛАХ/БУЦААХ үйлдэл хийж чадах эсэх.
const REVIEWER_GROUPS = [166, 174, 179, 192];

export default function useAccessScope() {
  const { user, isAdmin } = useUserContext();

  return useMemo(() => {
    const groups = new Set((user?.userGroupList || []).map((g) => Number(g)));
    const admin = !!(isAdmin && isAdmin());
    const canSeeAll = admin || FACTORY_GROUPS.some((g) => groups.has(g));
    const isCexLevel = CEX_GROUPS.some((g) => groups.has(g));
    const isBrigadeLevel =
      !canSeeAll && !isCexLevel && BRIGADE_GROUPS.some((g) => groups.has(g));
    // Батлах эрх — түвшингээс хамаарахгүй, тусад нь шалгагдана.
    const isReviewer = admin || REVIEWER_GROUPS.some((g) => groups.has(g));

    // userdepts (давхар array байж болно) — өөрийн харьяа нэгжийн кодууд
    const raw = user?.userdepts || user?.userdeps || [];
    const flat = Array.isArray(raw[0]) ? raw.flat() : raw;
    const deptCodes = new Set(
      flat.map((d) => d?.departmentcode || d?.department_code).filter(Boolean),
    );
    if (user?.info?.department_code) deptCodes.add(user.info.department_code);
    if (user?.info?.negj_code) deptCodes.add(user.info.negj_code);

    const tsehCode = user?.info?.tseh_code ? String(user.info.tseh_code) : "";
    const tn = String(user?.info?.tn || user?.tn || "");

    // Өөрийн хэсгийн (бригадын) код — анхан шатны удирдах ажилтанд гол шүүлт
    const sectionCode = String(
      user?.info?.negj_code || user?.info?.department_code || "",
    );

    const level = canSeeAll
      ? "factory"
      : isCexLevel
        ? "cex"
        : isBrigadeLevel
          ? "brigade"
          : "self";
    const levelLabel = canSeeAll
      ? "Үйлдвэрийн хэмжээ"
      : isCexLevel
        ? "Цех түвшин"
        : isBrigadeLevel
          ? "Хэсэг / бригад"
          : "Ажилтан";
    const scopeName = canSeeAll
      ? "Бүх нэгж"
      : isBrigadeLevel
        ? user?.info?.negj_namemn ||
          user?.info?.department_name ||
          "Өөрийн хэсэг"
        : user?.info?.tseh_namemn ||
          user?.info?.department_name ||
          "Өөрийн бүртгэл";

    const ownedByTn = (ownerTn) => ownerTn && String(ownerTn) === tn;

    /** Бригадын түвшний тохироо — хэсгийн код (аль ч талбарт байж болно) */
    const inMySection = (...codes) =>
      codes.some(
        (c) =>
          c &&
          (deptCodes.has(c) ||
            (sectionCode && String(c).startsWith(sectionCode))),
      );

    // Эрсдэл — department_code / section_code / insert_user_tn (List.js хэвээр)
    const matchRisk = (r) => {
      if (canSeeAll) return true;
      if (isCexLevel && tsehCode) {
        return (
          String(r.department_code || "").startsWith(tsehCode) ||
          deptCodes.has(r.department_code) ||
          ownedByTn(r.insert_user_tn)
        );
      }
      if (isBrigadeLevel) {
        return (
          inMySection(r.section_code, r.department_code) ||
          ownedByTn(r.insert_user_tn)
        );
      }
      return (
        deptCodes.has(r.department_code) ||
        deptCodes.has(r.section_code) ||
        ownedByTn(r.insert_user_tn)
      );
    };

    // Аюул — insert_dept_code / insert_user_tn
    const matchHazard = (h) => {
      if (canSeeAll) return true;
      if (isCexLevel && tsehCode) {
        return (
          String(h.insert_dept_code || "").startsWith(tsehCode) ||
          deptCodes.has(h.insert_dept_code) ||
          ownedByTn(h.insert_user_tn)
        );
      }
      if (isBrigadeLevel) {
        return inMySection(h.insert_dept_code) || ownedByTn(h.insert_user_tn);
      }
      return deptCodes.has(h.insert_dept_code) || ownedByTn(h.insert_user_tn);
    };

    // ӨЭА — цехийн код `department_code`-д, ХЭСГИЙН код `section_code`-д
    // хадгалагддаг. Тиймээс хэсгийн түвшний хэрэглэгчид ЗААВАЛ section_code-ыг
    // шалгах ёстой — эс бөгөөс бригадынхаа ганц ч бүртгэл харагдахгүй.
    const matchOea = (o) => {
      if (canSeeAll) return true;
      if (isCexLevel && tsehCode) {
        return (
          String(o.department_code || "").startsWith(tsehCode) ||
          deptCodes.has(o.department_code)
        );
      }
      if (isBrigadeLevel) {
        return inMySection(o.section_code, o.department_code);
      }
      return (
        deptCodes.has(o.department_code) || deptCodes.has(o.section_code)
      );
    };

    return {
      canSeeAll,
      isCexLevel,
      isBrigadeLevel,
      isReviewer,
      level,
      levelLabel,
      scopeName,
      tsehCode,
      sectionCode,
      deptCodes,
      tn,
      matchRisk,
      matchHazard,
      matchOea,
    };
  }, [user, isAdmin]);
}
